import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'
import { Clock } from 'lucide-react'

/**
 * D-specific pending approval page.
 * Shown to users who have signed up but haven't been approved yet.
 * Note: D shares approval with Z - approval for Z grants access to D.
 */
export function DPendingApprovalPage() {
  const navigate = useNavigate()
  const {
    user,
    approvalStatus,
    approvalChecked,
    emailVerified,
    signOut,
    checkApprovalStatus,
    initialize,
  } = useAuthStore()

  // Initialize auth on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect if not authenticated
  useEffect(() => {
    if (!user && approvalChecked) {
      navigate('/d/login', { replace: true })
    }
  }, [user, approvalChecked, navigate])

  // Redirect if approved - to verify-pending if email not verified, otherwise to app
  useEffect(() => {
    if (approvalStatus === 'approved') {
      if (emailVerified) {
        navigate('/d', { replace: true })
      } else {
        navigate('/d/verify-pending', { replace: true })
      }
    }
  }, [approvalStatus, emailVerified, navigate])

  // Periodically check approval status (every 30 seconds)
  useEffect(() => {
    if (!user) return

    const interval = setInterval(() => {
      checkApprovalStatus()
    }, 30000)

    return () => clearInterval(interval)
  }, [user, checkApprovalStatus])

  const handleSignOut = async () => {
    await signOut()
    navigate('/d/login', { replace: true })
  }

  const handleRefresh = () => {
    checkApprovalStatus()
  }

  if (!user || !approvalChecked) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">
            Loading...
          </p>
        </div>
      </div>
    )
  }

  if (approvalStatus === 'rejected') {
    return (
      <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
        {/* Logo / Brand */}
        <div className="mb-12 text-center">
          <h1 className="text-2xl font-light tracking-tight text-foreground">
            Zinsser
          </h1>
          <p className="mt-2 text-sm text-muted-foreground font-light">
            Write with clarity
          </p>
        </div>

        <div className="w-full max-w-sm text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
            <span className="text-2xl">✕</span>
          </div>
          <h2 className="text-xl font-light tracking-tight text-foreground mb-4">
            Access Denied
          </h2>
          <p className="text-sm text-muted-foreground font-light mb-8">
            Your account request has been declined. If you believe this is an error,
            please contact support.
          </p>
          <button
            onClick={handleSignOut}
            className="w-full px-4 py-2.5 rounded-md border border-border hover:bg-muted text-sm font-light transition-colors"
            style={{ minHeight: '44px' }}
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // Pending state
  return (
    <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
      {/* Logo / Brand */}
      <div className="mb-12 text-center">
        <h1 className="text-2xl font-light tracking-tight text-foreground">
          Zinsser
        </h1>
        <p className="mt-2 text-sm text-muted-foreground font-light">
          Write with clarity
        </p>
      </div>

      <div className="w-full max-w-sm text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-6">
          <Clock className="w-8 h-8 text-amber-500" />
        </div>
        <h2 className="text-xl font-light tracking-tight text-foreground mb-4">
          Awaiting Approval
        </h2>
        <p className="text-sm text-muted-foreground font-light mb-2">
          Thanks for signing up, <span className="text-foreground">{user.email}</span>!
        </p>
        <p className="text-sm text-muted-foreground font-light mb-8">
          Your account is pending approval. You'll receive an email once your access
          has been granted.
        </p>

        <div className="space-y-3">
          <button
            onClick={handleRefresh}
            className="w-full flex items-center justify-center bg-foreground text-background hover:bg-foreground/90 px-4 py-2.5 rounded-md transition-colors"
            style={{ minHeight: '44px' }}
          >
            <span className="text-sm font-light">Check status</span>
          </button>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center border border-border hover:bg-muted px-4 py-2.5 rounded-md transition-colors"
            style={{ minHeight: '44px' }}
          >
            <span className="text-sm font-light">Sign out</span>
          </button>
        </div>

        <p className="mt-8 text-xs text-muted-foreground/70 font-light">
          This page will automatically check for approval every 30 seconds
        </p>
      </div>
    </div>
  )
}
